<script lang="ts">
  import { Trophy, ChevronLeft, RotateCcw, CheckCircle, XCircle, SkipForward } from 'lucide-svelte';
  import { scale, fade, fly } from 'svelte/transition';
  import Button from '../ui/button.svelte';
  import Card from '../ui/card.svelte';
  import type { ExerciseSession, ExerciseMode } from '../../types/exercise';
  import { calculateSessionSummary } from '../../lib/exercise-engine';
  import { _ } from 'svelte-i18n';

  interface Props {
    session: ExerciseSession;
    onBack: () => void;
    onRestart: () => void;
  }

  let { session, onBack, onRestart }: Props = $props();

  // Calculate summary
  let summary = $derived(calculateSessionSummary(session));

  // Get emoji based on score
  function getScoreEmoji(score: number): string {
    if (score >= 90) return '🏆';
    if (score >= 70) return '🎉';
    if (score >= 50) return '👍';
    return '💪';
  }

  // Get mode display name
  function getModeName(mode: ExerciseMode): string {
    const names: Record<ExerciseMode, string> = {
      TYPE_IN: 'Psaní',
      MULTIPLE_CHOICE: 'Výběr',
      MATCH_PAIRS: 'Párování',
      LISTEN_TYPE: 'Poslech',
      CLOZE_SMART: 'Doplňování',
      WORD_BUILD: 'Skládání',
    };
    return names[mode];
  }

  // Format time
  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  }
</script>

<div class="min-h-screen bg-background pb-20">
  <div class="container mx-auto px-4 py-6 max-w-md">
    <!-- Header -->
    <div class="mb-8 text-center" in:scale={{ duration: 400, start: 0.9 }}>
      <div
        class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 mb-4 ring-8 ring-primary/5 shadow-xl animate-bounce"
      >
        <Trophy class="w-12 h-12 text-primary" />
      </div>
      <h1 class="text-3xl font-bold mb-2 tracking-tight">
        {$_('exercise.exercise_completed')} <span class="animate-pulse inline-block">{getScoreEmoji(summary.scorePercentage)}</span>
      </h1>
      <p class="text-muted-foreground">{$_('exercise.results_here')}</p>
    </div>

    <!-- Score card -->
    <Card class="p-8 mb-8 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-primary/20 shadow-lg relative overflow-hidden">
      <!-- Decoration -->
      <div class="absolute -right-10 -top-10 w-40 h-40 bg-primary/20 blur-3xl rounded-full pointer-events-none"></div>
      
      <div class="text-center relative z-10">
        <div class="text-7xl font-black mb-2 tracking-tighter text-primary">
          {Math.round(summary.scorePercentage)}<span class="text-4xl align-top opacity-60">%</span>
        </div>
        <div class="text-lg font-medium text-muted-foreground">
          {$_('exercise.correct_out_of_total', { values: { correct: summary.correctCount, total: summary.totalExercises } })}
        </div>
      </div>
    </Card>

    <div class="space-y-6" in:fly={{ y: 20, duration: 500, delay: 200 }}>
      <!-- Statistics -->
      <Card class="p-6 bg-card/50 backdrop-blur-sm">
        <h3 class="font-semibold mb-4 text-lg">{$_('exercise.statistics')}</h3>
        <div class="space-y-4">
          <div class="flex justify-between items-center p-2 rounded-lg hover:bg-secondary/20 transition-colors">
            <span class="text-muted-foreground flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center">
                    <span class="text-xs">⏱️</span>
                </div>
                {$_('exercise.total_time')}
            </span>
            <span class="font-mono font-semibold">{formatTime(summary.totalTimeTaken)}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 rounded-lg hover:bg-secondary/20 transition-colors">
            <span class="text-muted-foreground flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle class="w-4 h-4 text-green-600" />
                </div>
                {$_('buttons.correct_answers')}
            </span>
            <span class="font-bold text-green-600 dark:text-green-400">{summary.correctCount}</span>
          </div>
          
          <div class="flex justify-between items-center p-2 rounded-lg hover:bg-secondary/20 transition-colors">
            <span class="text-muted-foreground flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                     <XCircle class="w-4 h-4 text-red-600" />
                </div>
                {$_('exercise.incorrect_answers')}
            </span>
            <span class="font-bold text-red-600 dark:text-red-400">{summary.incorrectCount}</span>
          </div>
          
          {#if summary.skippedCount > 0}
            <div class="flex justify-between items-center p-2 rounded-lg hover:bg-secondary/20 transition-colors">
              <span class="text-muted-foreground flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center">
                    <SkipForward class="w-4 h-4 text-yellow-600" />
                </div>
                {$_('exercise.skipped')}
              </span>
              <span class="font-bold text-yellow-600 dark:text-yellow-400">{summary.skippedCount}</span>
            </div>
          {/if}
        </div>
      </Card>

      <!-- Mode breakdown -->
      <!-- Only show if there are multiple modes or interesting stats -->
      {#if Object.keys(summary.modeBreakdown).length > 1}
        <Card class="p-6 bg-card/50 backdrop-blur-sm">
          <h3 class="font-semibold mb-4 text-lg">{$_('exercise.results_by_type')}</h3>
          <div class="space-y-4">
            {#each Object.entries(summary.modeBreakdown) as [mode, stats]}
              {#if stats.total > 0}
                <div class="flex items-center justify-between gap-4">
                  <span class="text-sm font-medium w-24">{getModeName(mode as ExerciseMode)}</span>
                  <div class="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      class="bg-primary h-full rounded-full"
                      style="width: {(stats.correct / stats.total) * 100}%"
                    ></div>
                  </div>
                  <span class="text-sm font-mono text-muted-foreground w-12 text-right">
                    {stats.correct}/{stats.total}
                  </span>
                </div>
              {/if}
            {/each}
          </div>
        </Card>
      {/if}

      <!-- Answer review -->
      <Card class="p-6 bg-card/50 backdrop-blur-sm">
        <h3 class="font-semibold mb-4 text-lg">{$_('exercise.answer_review')}</h3>
        <div class="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
          {#each session.results as result, i}
            <div
              class="flex items-start gap-4 p-4 rounded-xl border transition-all hover:scale-[1.01]
                     {result.isCorrect
                       ? 'border-green-500/20 bg-green-50/50 dark:bg-green-500/20 hover:border-green-500/40'
                       : result.skipped
                         ? 'border-yellow-500/20 bg-yellow-50/50 dark:bg-yellow-900/10 hover:border-yellow-500/40'
                         : 'border-red-500/20 bg-red-50/50 dark:bg-red-900/10 hover:border-red-500/40'}"
            >
              <div class="mt-0.5 shrink-0">
                {#if result.isCorrect}
                  <CheckCircle class="w-6 h-6 text-green-600 dark:text-green-400" />
                {:else if result.skipped}
                  <SkipForward class="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                {:else}
                  <XCircle class="w-6 h-6 text-red-600 dark:text-red-400" />
                {/if}
              </div>
              <div class="flex-1 min-w-0 space-y-1">
                <div class="flex items-center justify-between gap-2">
                    <div class="font-medium text-base leading-tight">
                    {session.exercises[i]?.prompt}
                    </div>
                </div>
                
                <div class="text-sm text-muted-foreground mt-2">
                   <div class="flex items-center gap-2">
                     <span class="text-xs uppercase tracking-wide opacity-70">{$_('exercise.correct_answer')}:</span>
                     <span class="font-bold text-foreground">{result.correctAnswer}</span>
                   </div>
                </div>

                {#if !result.isCorrect && result.userAnswer && !result.skipped}
                  <div class="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <span class="text-xs uppercase tracking-wide opacity-70">{$_('exercise.your_answer')}:</span>
                    <span class="font-medium strike-through">{result.userAnswer}</span>
                  </div>
                {/if}
                
                {#if result.skipped}
                  <div class="text-xs text-yellow-600 dark:text-yellow-400 italic">{$_('exercise.skipped_label')}</div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </Card>

      <!-- Actions -->
      <div class="flex gap-4 pt-4">
        <button 
            type="button" 
            class="btn btn-back flex-1 h-12 text-base hover:bg-secondary/80" 
            onclick={() => onBack()}
        >
          <ChevronLeft class="w-5 h-5 mr-2" />
          {$_('buttons.back')}
        </button>
        <Button onclick={onRestart} variant="default" class="flex-1 h-12 text-base shadow-lg shadow-primary/20">
          <RotateCcw class="w-5 h-5 mr-2" />
          {$_('exercise.restart')}
        </Button>
      </div>
    </div>
  </div>
</div>
